import { just, Maybe, nothing } from 'maybeasy';
import { AppyError } from '../../../../Appy';
import { Error, ServerError } from '../../../../ErrorHandling';
import { MissingLinkError } from '../../../../LinkyLinky';
import { Resource } from '../../../../Resource/Types';
import { TPlainTextKey } from '../../../../Translations';
import { SelectableUserGroup } from '../../../Form/GroupInputWithSuggestions/Types';
import { UserGroupWithAssociations } from '../../Participants/AddParticipant/Stores/ParticipantOptionsStore/Types';

export type ImportParticipantsV2Error = AppyError | MissingLinkError | Error | ServerError;

// Participant row from HelloCSV
export interface ParticipantCsvRow {
  email: string;
  firstname: string;
  lastname: string;
  login_type: Maybe<string>;
}

// License type (same as V2 single participant)
export interface License {
  id: number;
  licenseName: Maybe<string>;
  licenseTypeName: string;
  quantityAvailable: number;
  startOn: Date;
  endOn: Date;
}

export interface UnselectedLicense {
  kind: 'unselected';
}

export interface SelectedLicense {
  kind: 'selected';
  license: License;
}

export interface SelectedAsAuditorLicense {
  kind: 'selected-as-auditor';
  license: License;
}

export type SelectableLicense = UnselectedLicense | SelectedLicense | SelectedAsAuditorLicense;

export const createUnselectedLicense = (): UnselectedLicense => ({ kind: 'unselected' });

export const createSelectedLicense = (license: License): SelectedLicense => ({
  kind: 'selected',
  license,
});

export const createSelectedAsAuditorLicense = (license: License): SelectedAsAuditorLicense => ({
  kind: 'selected-as-auditor',
  license,
});

export const isSelectedLicense = (
  license: SelectableLicense,
): license is SelectedLicense | SelectedAsAuditorLicense =>
  license.kind === 'selected' || license.kind === 'selected-as-auditor';

// The auditor toggle applies to the whole selection, so any auditor selection
// makes the import an auditor import.
export const areLicensesAuditor = (licenses: SelectableLicense[]): boolean =>
  licenses.some((license) => license.kind === 'selected-as-auditor');

// Import options (groups and licenses available for selection)
export interface ImportOptions {
  userGroups: UserGroupWithAssociations[];
  licenses: License[];
  canAddAsAuditor: boolean;
  allowedEmailDomains: string[];
}

export type ImportOptionsResource = Resource<ImportOptions>;

// Import result types
interface ParticipantBase {
  email: string;
  firstName: string;
  lastName: string;
}

export interface FailedParticipant extends ParticipantBase {
  errorMessage: string;
  kind: 'failed';
}

export interface SuccessfulParticipant extends ParticipantBase {
  kind: 'successful';
  id: number;
}

export interface UpdatedParticipant extends ParticipantBase {
  kind: 'updated';
  id: number;
}

export interface DomainMismatchAlert {
  email: string;
  message: string;
}

export interface ImportResult {
  successfulParticipants: SuccessfulParticipant[];
  updatedParticipants: UpdatedParticipant[];
  failedParticipants: FailedParticipant[];
  participantsOfSameOrg: FailedParticipant[];
  participantsOfDifferentOrg: FailedParticipant[];
  formattingIssue: FailedParticipant[];
  domainMismatchAlerts: DomainMismatchAlert[];
  summary: {
    total: number;
    successful: number;
    updated: number;
    failed: number;
  };
}

export type ImportResultResource = Resource<ImportResult>;

// State machine states
interface Waiting {
  kind: 'waiting';
}

interface LoadingOptions {
  kind: 'loading-options';
}

interface OptionsReady {
  kind: 'options-ready';
  optionsResource: ImportOptionsResource;
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
}

interface HelloCsvOpen {
  kind: 'hellocsv-open';
  optionsResource: ImportOptionsResource;
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
  participants: Maybe<ParticipantCsvRow[]>;
  filename: Maybe<string>;
}

interface FileProcessed {
  kind: 'file-processed';
  optionsResource: ImportOptionsResource;
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
  participants: ParticipantCsvRow[];
  filename: string;
}

interface Submitting {
  kind: 'submitting';
  optionsResource: ImportOptionsResource;
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
  participants: ParticipantCsvRow[];
  filename: string;
}

interface SubmitFailed {
  kind: 'submit-failed';
  optionsResource: ImportOptionsResource;
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
  participants: ParticipantCsvRow[];
  filename: string;
  message: TPlainTextKey;
}

interface SubmitSucceeded {
  kind: 'submit-succeeded';
  optionsResource: ImportOptionsResource;
  filename: string;
  resultResource: ImportResultResource;
  // Preserve for display behind modal
  participants: ParticipantCsvRow[];
  selectedGroups: SelectableUserGroup[];
  selectedLicenses: SelectableLicense[];
}

export type ViewReportAction =
  | 'full-view'
  | 'success-view'
  | 'updated-view'
  | 'already-exist-in-same-org'
  | 'already-exist-in-diff-org'
  | 'formatting-issue'
  | 'import-failed'
  | 'domain-mismatch';

interface ViewReport {
  kind: 'view-report';
  optionsResource: ImportOptionsResource;
  filename: string;
  resultResource: ImportResultResource;
  view: ViewReportAction;
}

interface OptionsLoadError {
  kind: 'options-load-error';
  message: TPlainTextKey;
}

export type State =
  | Waiting
  | LoadingOptions
  | OptionsReady
  | HelloCsvOpen
  | FileProcessed
  | Submitting
  | SubmitFailed
  | SubmitSucceeded
  | ViewReport
  | OptionsLoadError
  | Error;

// State constructors
export const waiting = (): Waiting => ({
  kind: 'waiting',
});

export const loadingOptions = (): LoadingOptions => ({
  kind: 'loading-options',
});

export const optionsReady = (optionsResource: ImportOptionsResource): OptionsReady => ({
  kind: 'options-ready',
  optionsResource,
  selectedGroups: [],
  selectedLicenses: [],
});

export const helloCsvOpen = (
  state: OptionsReady | HelloCsvOpen | FileProcessed | SubmitFailed,
): HelloCsvOpen => ({
  kind: 'hellocsv-open',
  optionsResource: state.optionsResource,
  selectedGroups: state.selectedGroups,
  selectedLicenses: state.selectedLicenses,
  participants:
    state.kind === 'file-processed' || state.kind === 'submit-failed'
      ? just(state.participants)
      : nothing(),
  filename:
    state.kind === 'file-processed' || state.kind === 'submit-failed'
      ? just(state.filename)
      : nothing(),
});

export const fileProcessed = (
  state: HelloCsvOpen,
  participants: ParticipantCsvRow[],
  filename: string,
): FileProcessed => ({
  kind: 'file-processed',
  optionsResource: state.optionsResource,
  selectedGroups: state.selectedGroups,
  selectedLicenses: state.selectedLicenses,
  participants,
  filename,
});

export const submitting = (state: FileProcessed | SubmitFailed): Submitting => ({
  kind: 'submitting',
  optionsResource: state.optionsResource,
  selectedGroups: state.selectedGroups,
  selectedLicenses: state.selectedLicenses,
  participants: state.participants,
  filename: state.filename,
});

export const submitFailed = (state: Submitting, message: TPlainTextKey): SubmitFailed => ({
  kind: 'submit-failed',
  optionsResource: state.optionsResource,
  selectedGroups: state.selectedGroups,
  selectedLicenses: state.selectedLicenses,
  participants: state.participants,
  filename: state.filename,
  message,
});

export const submitSucceeded = (
  state: Submitting,
  resultResource: ImportResultResource,
): SubmitSucceeded => ({
  kind: 'submit-succeeded',
  optionsResource: state.optionsResource,
  filename: state.filename,
  resultResource,
  participants: state.participants,
  selectedGroups: state.selectedGroups,
  selectedLicenses: state.selectedLicenses,
});

export const viewReport = (state: SubmitSucceeded, view: ViewReportAction): ViewReport => ({
  kind: 'view-report',
  optionsResource: state.optionsResource,
  filename: state.filename,
  resultResource: state.resultResource,
  view,
});

export const optionsLoadError = (message: TPlainTextKey): OptionsLoadError => ({
  kind: 'options-load-error',
  message,
});
